import React, { useRef, useState } from 'react'
import { Outlet } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { F1SearchBox } from '@factorialco/factorial-one-react/dist/experimental'
import { Spinner } from '@factorialco/factorial-one-react/dist/experimental'
import { JobCatalogTreePageDocument } from 'generated/resources/graphql'

import { useEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import JobCatalogSearchResultsNavigator from 'modules/job_catalog/components/JobCatalogSearchResultsNavigator'
import JobCatalogTree from 'modules/job_catalog/components/JobCatalogTree'
import { SerializedTreeNode } from 'modules/job_catalog/pages/JobCatalogTreePage/types'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

const LoadingSpinner = () => (
  <Box height="full" alignItems="center" justifyContent="center">
    <Spinner />
  </Box>
)

interface FlattenedNode {
  id: string
  name: string
  type: string
  node_uuid: string
}

const flattenTreeNodes = (node: SerializedTreeNode): FlattenedNode[] => {
  const result: FlattenedNode[] = []

  const processNode = (currentNode: SerializedTreeNode) => {
    result.push({
      id: currentNode.node_uuid,
      name: currentNode.name,
      type: currentNode.type,
      node_uuid: currentNode.node_uuid,
    })

    if (currentNode.children) {
      currentNode.children.forEach(processNode)
    }
  }

  processNode(node)
  return result
}

const JobCatalogTreePage = () => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [dimensions, setDimensions] = useState({
    width: 0,
    height: 0,
  })
  const [search, setSearch] = useState('')
  const [selectedNodeId, setSelectedNodeId] = useState<string | undefined>()
  const { data, loading } = useQuery(JobCatalogTreePageDocument)

  const treeData = data?.jobCatalog.treesConnection.nodes[0]

  const flattenedNodes = useUnsafeMemo(() => {
    if (!treeData?.serializedTree) return []
    try {
      const parsed = JSON.parse(treeData.serializedTree)
      const tree: SerializedTreeNode = Array.isArray(parsed) ? parsed[0] : parsed
      if (!tree) return []
      return flattenTreeNodes(tree)
    } catch {
      return []
    }
  }, [treeData?.serializedTree])

  const results = useUnsafeMemo(() => {
    if (!search.trim() || flattenedNodes.length === 0) return []

    const searchTerm = search.toLowerCase().trim()
    return flattenedNodes
      .filter((node) => node.name.toLowerCase().includes(searchTerm))
      .map((node) => ({
        ...node,
        score: 1,
        id: node.node_uuid,
      }))
  }, [flattenedNodes, search])

  const handleResultChange = (result: (typeof results)[0] | undefined) => {
    if (result) {
      setSelectedNodeId(result.node_uuid)
      setTimeout(() => {
        setSelectedNodeId(undefined)
      }, 1000)
    }
  }

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const { offsetWidth, offsetHeight } = containerRef.current
        setDimensions({ width: offsetWidth, height: offsetHeight })
      }
    }
    updateSize()
    window.addEventListener('resize', updateSize)
    return () => window.removeEventListener('resize', updateSize)
  }, [])

  const { width, height } = dimensions

  return (
    <ResponsivePage.Body variant="fullscreen">
      <Box
        width="full"
        paddingX="s20"
        paddingY="s16"
        flexDirection="row"
        alignItems="center"
        justifyContent="spaceBetween"
        gap="s8"
        border={{
          bottom: {
            color: 'grey200',
            width: 's1',
            style: 'solid',
          },
        }}
      >
        <Box flexDirection="row" alignItems="center" justifyContent="spaceBetween" width="full">
          <Box flexDirection="row" alignItems="center" gap="s8">
            <F1SearchBox
              placeholder={i18n.t('common.search')}
              value={search}
              onChange={setSearch}
              clearable
              autoFocus
            />
            <Box flexNoShrink>
              <JobCatalogSearchResultsNavigator
                results={results}
                search={search}
                onResultChange={handleResultChange}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      <Box position="relative" width="full" height="full" ref={containerRef}>
        {loading ? (
          <LoadingSpinner />
        ) : treeData && width > 0 && height > 0 ? (
          <JobCatalogTree
            width={width}
            height={height}
            treeData={treeData}
            zoomToNodeId={selectedNodeId}
          />
        ) : null}
        <React.Suspense fallback={<LoadingSpinner />}>
          <Outlet />
        </React.Suspense>
      </Box>
    </ResponsivePage.Body>
  )
}

export default JobCatalogTreePage
